import { Box, Button, ExpandableSection, Link, SpaceBetween } from '@amzn/awsui-components-react';
import { LoadingBar } from '@amzn/awsui-chat-components-console';
import { useLocalizedText } from '../../../../hooks/localizedText';
import { WritingAssistantState, RewriteRejection } from '../../../../utils/aiContentTypes';
import { markdownToHtml } from '../../../../utils/markdownToHtml';
import './writingAssistant.scss';

interface WritingAssistantPanelProps {
  state: WritingAssistantState;
  canRewrite: boolean;
  rewriteDisabledReason?: string;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
  rewrittenDescription?: string;
  tips?: string;
  isTipsPending?: boolean;
  tipsError?: boolean;
  errorMessage?: string;
  /** False whenever `rewriteRejection` is set; turns off Accept so it can't be used. */
  isRewriteValid?: boolean;
  rewriteRejection?: RewriteRejection;
  onRewrite: () => void;
  onAccept: () => void;
  onCancel: () => void;
  onCancelGeneration: () => void;
  onGenerateAnother: () => void;
}

// `default` covers a rewrite that came back empty, where there is no reason to give beyond that
// there is nothing to accept.
const REWRITE_REJECTION_MESSAGES: Record<RewriteRejection | 'default', string> = {
  notAnEventDescription: 'writingAssistant.cannotAcceptRewrite',
  tooLongForField: 'writingAssistant.cannotAcceptLongRewrite',
  default: 'writingAssistant.cannotAcceptRewrite',
};

export function WritingAssistantPanel({
  state,
  canRewrite,
  rewriteDisabledReason,
  isCollapsed,
  onToggleCollapse,
  rewrittenDescription,
  tips,
  isTipsPending = false,
  tipsError = false,
  errorMessage,
  isRewriteValid = true,
  rewriteRejection,
  onRewrite,
  onAccept,
  onCancel,
  onCancelGeneration,
  onGenerateAnother,
}: WritingAssistantPanelProps) {
  const { t } = useLocalizedText(['events', 'translation']);

  if (isCollapsed) {
    return (
      <button
        className="writing-assistant writing-assistant--collapsed"
        data-testid="writing-assistant-panel-collapsed"
        onClick={onToggleCollapse}
        aria-label={t('writingAssistant.expand')}
        type="button"
      >
        <span className="writing-assistant__collapsed-icon" aria-hidden="true">
          ✦
        </span>
        <span className="writing-assistant__collapsed-label">{t('writingAssistant.title')}</span>
      </button>
    );
  }

  return (
    <div className="writing-assistant" data-testid="writing-assistant-panel">
      <div className="writing-assistant__header">
        <div className="writing-assistant__header-row">
          <SpaceBetween direction="horizontal" size="xs" alignItems="center">
            <span className="writing-assistant__icon" aria-hidden="true">
              ✦
            </span>
            <Box variant="h4" margin={{ bottom: 'n' }}>
              {t('writingAssistant.title')}
            </Box>
          </SpaceBetween>
          <Button
            variant="icon"
            iconName="close"
            ariaLabel={t('writingAssistant.collapse')}
            onClick={onToggleCollapse}
          />
        </div>
      </div>

      <div className="writing-assistant__content" aria-live="polite">
        {state === 'idle' && <IdleContent />}
        {state === 'loading' && <LoadingContent />}
        {state === 'result' && (
          <ResultContent
            rewrittenDescription={rewrittenDescription ?? ''}
            tips={tips ?? ''}
            isTipsPending={isTipsPending}
            tipsError={tipsError}
          />
        )}
        {state === 'tips' && (
          <TipsContent tips={tips ?? ''} isTipsPending={isTipsPending} tipsError={tipsError} />
        )}
        {state === 'error' && <ErrorContent errorMessage={errorMessage} />}
      </div>

      <div className="writing-assistant__footer">
        {state === 'loading' && (
          <Button variant="link" onClick={onCancelGeneration} data-testid="cancel-generation-btn">
            {t('writingAssistant.cancel')}
          </Button>
        )}
        {(state === 'idle' || state === 'tips' || state === 'error') && (
          <Button
            variant="primary"
            disabled={!canRewrite}
            disabledReason={rewriteDisabledReason ?? t('writingAssistant.minCharsHint')}
            onClick={onRewrite}
            iconName="gen-ai"
            data-testid="rewrite-description-btn"
          >
            {t('writingAssistant.rewriteButton')}
          </Button>
        )}
        {state === 'result' && (
          <SpaceBetween direction="horizontal" size="s">
            <Button variant="link" onClick={onCancel} data-testid="cancel-rewrite-btn">
              {t('writingAssistant.cancel')}
            </Button>
            <Button
              variant="normal"
              iconName="refresh"
              disabled={!canRewrite}
              disabledReason={rewriteDisabledReason ?? t('writingAssistant.minCharsHint')}
              onClick={onGenerateAnother}
              data-testid="generate-another-btn"
            >
              {t('writingAssistant.generateAnother')}
            </Button>
            <Button
              variant="primary"
              disabled={!isRewriteValid || !rewrittenDescription}
              disabledReason={t(REWRITE_REJECTION_MESSAGES[rewriteRejection ?? 'default'])}
              onClick={onAccept}
              data-testid="accept-rewrite-btn"
            >
              {t('writingAssistant.acceptRewrite')}
            </Button>
          </SpaceBetween>
        )}
      </div>
    </div>
  );
}

function IdleContent() {
  const { t } = useLocalizedText(['events', 'translation']);

  return (
    <div className="writing-assistant__guidance">
      <Box variant="p" fontWeight="bold">
        {t('writingAssistant.guidanceHeader')}
      </Box>
      <ul className="writing-assistant__guidance-list">
        <li>
          <strong>{t('writingAssistant.guidanceBenefits')}</strong>{' '}
          {t('writingAssistant.guidanceBenefitsDetail')}
        </li>
        <li>
          <strong>{t('writingAssistant.guidanceTasks')}</strong>{' '}
          {t('writingAssistant.guidanceTasksDetail')}
        </li>
        <li>
          <strong>{t('writingAssistant.guidanceLogistics')}</strong>{' '}
          {t('writingAssistant.guidanceLogisticsDetail')}
        </li>
        <li>
          <strong>{t('writingAssistant.guidancePreWork')}</strong>{' '}
          {t('writingAssistant.guidancePreWorkDetail')}
        </li>
        <li>
          <strong>{t('writingAssistant.guidanceFaqs')}</strong>{' '}
          {t('writingAssistant.guidanceFaqsDetail')}
        </li>
      </ul>
      <Box variant="p" color="text-body-secondary">
        {t('writingAssistant.guidanceFooter')}
      </Box>
    </div>
  );
}

function LoadingContent() {
  const { t } = useLocalizedText(['events', 'translation']);

  return (
    <div className="writing-assistant__loading" data-testid="writing-assistant-loading">
      <Box variant="p">{t('writingAssistant.rewritingDescription')}</Box>
      <LoadingBar variant="gen-ai" />
    </div>
  );
}

const AI_FEEDBACK_URL =
  'https://sim.amazon.com/issues/create?template=a2a5eaa0-5b78-4070-9475-c11315d94ecc';

function FeedbackLink() {
  const { t } = useLocalizedText(['events', 'translation']);

  return (
    <Box variant="p">
      <strong>{t('writingAssistant.feedbackLabelBold')}</strong>{' '}
      <Link href={AI_FEEDBACK_URL} external variant="primary">
        {t('writingAssistant.feedbackLinkText')}
      </Link>
    </Box>
  );
}

interface TipsProps {
  tips: string;
  isTipsPending: boolean;
  tipsError: boolean;
}

function TipsSection({ tips, isTipsPending, tipsError }: TipsProps) {
  const { t } = useLocalizedText(['events', 'translation']);

  if (!tips && !isTipsPending && !tipsError) return null;

  // Open on first render, so the coordinator sees the tips arriving instead of having to go looking
  // for them. Collapsing it afterwards is theirs to keep: this only sets the starting state.
  return (
    <ExpandableSection
      headerText={t('writingAssistant.tipsHeader')}
      variant="footer"
      defaultExpanded
      data-testid="tips-section"
    >
      <TipsBody tips={tips} isTipsPending={isTipsPending} tipsError={tipsError} />
    </ExpandableSection>
  );
}

function TipsBody({ tips, isTipsPending, tipsError }: TipsProps) {
  const { t } = useLocalizedText(['events', 'translation']);

  if (isTipsPending) {
    return (
      <div className="writing-assistant__loading" data-testid="tips-loading">
        <Box variant="p">{t('writingAssistant.tipsLoading')}</Box>
        <LoadingBar variant="gen-ai" />
      </div>
    );
  }

  if (tipsError) {
    return (
      <Box variant="p" color="text-status-error" data-testid="tips-error">
        {t('writingAssistant.tipsUnavailable')}
      </Box>
    );
  }

  return (
    <div
      className="writing-assistant__tips-content"
      dangerouslySetInnerHTML={{ __html: markdownToHtml(tips) }}
    />
  );
}

function ResultContent({
  rewrittenDescription,
  ...tipsProps
}: TipsProps & { rewrittenDescription: string }) {
  const { t } = useLocalizedText(['events', 'translation']);

  return (
    <SpaceBetween direction="vertical" size="m">
      <div className="writing-assistant__suggested-rewrite">
        <Box variant="small" color="text-status-warning" fontWeight="bold">
          <SpaceBetween direction="horizontal" size="xxs" alignItems="center">
            <span className="writing-assistant__icon" aria-hidden="true">
              ✦
            </span>
            {t('writingAssistant.suggestedRewrite')}
          </SpaceBetween>
        </Box>
        <div
          className="writing-assistant__rewrite-preview"
          data-testid="rewrite-preview"
          dangerouslySetInnerHTML={{ __html: markdownToHtml(rewrittenDescription) }}
        />
      </div>

      <FeedbackLink />
      <TipsSection {...tipsProps} />
    </SpaceBetween>
  );
}

function TipsContent(tipsProps: TipsProps) {
  return (
    <SpaceBetween direction="vertical" size="m">
      <FeedbackLink />
      <TipsSection {...tipsProps} />
    </SpaceBetween>
  );
}

function ErrorContent({ errorMessage }: { errorMessage?: string }) {
  const { t } = useLocalizedText(['events', 'translation']);

  return (
    <Box variant="p" color="text-status-error">
      {errorMessage ?? t('writingAssistant.genericError')}
    </Box>
  );
}
